import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  Gift,
  Package,
  MapPin,
  Clock,
  Sparkles,
  CheckCircle2,
  AlertCircle,
  Smartphone,
  Navigation,
  ArrowRight,
  Store,
  ShieldCheck,
} from 'lucide-react';
import { GiftService, GiftClaimDetails } from '../../services/gift.service';

export const GiftClaim: React.FC = () => {
  const { token } = useParams<{ token: string }>();

  const [loading, setLoading] = useState<boolean>(true);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [giftDetails, setGiftDetails] = useState<GiftClaimDetails | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isExpired, setIsExpired] = useState<boolean>(false);
  const [isClaimed, setIsClaimed] = useState<boolean>(false);
  const [showAppModal, setShowAppModal] = useState<boolean>(false);

  // Form state
  const [street, setStreet] = useState<string>('');
  const [city, setCity] = useState<string>('Lagos');
  const [state, setState] = useState<string>('Lagos');
  const [notes, setNotes] = useState<string>('');
  const [coordinates, setCoordinates] = useState<[number, number]>([3.4735, 6.4474]); // Default Lagos coordinates
  const [locating, setLocating] = useState<boolean>(false);

  useEffect(() => {
    if (!token) {
      setErrorMessage('No claim token was provided in the URL.');
      setLoading(false);
      return;
    }

    const fetchDetails = async () => {
      setLoading(true);
      setErrorMessage(null);

      const res = await GiftService.getClaimDetails(token);

      if (!res.success) {
        setErrorMessage(res.message);
        if (res.message.toLowerCase().includes('expired')) {
          setIsExpired(true);
        }
      } else if (res.data) {
        setGiftDetails(res.data);
        if (res.data.isClaimed || res.data.status !== 'awaiting_gift_claim') {
          setIsClaimed(true);
        }
      }
      setLoading(false);
    };

    fetchDetails();
  }, [token]);

  // Handle Browser Geolocation
  const handleAutoLocate = () => {
    if (!navigator.geolocation) {
      alert('Geolocation is not supported by your browser.');
      return;
    }

    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      position => {
        setCoordinates([position.coords.longitude, position.coords.latitude]);
        setLocating(false);
      },
      error => {
        console.warn('Geolocation error:', error);
        setLocating(false);
        alert('Could not automatically retrieve your location. Please enter your address manually.');
      },
      { timeout: 10000, enableHighAccuracy: true },
    );
  };

  // Handle Deep Linking to Mobile App
  const handleOpenInApp = () => {
    if (!token) return;

    const deepLinkUrl = `marketmate://gift/claim/${encodeURIComponent(token)}`;
    const startTime = Date.now();

    // Attempt to open native scheme
    window.location.href = deepLinkUrl;

    // If app is not installed (no blur after 1.5s), show web helper modal
    setTimeout(() => {
      if (Date.now() - startTime < 2000) {
        setShowAppModal(true);
      }
    }, 1500);
  };

  // Submit address claim
  const handleSubmitClaim = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!token) return;
    if (!street.trim()) {
      alert('Please enter your delivery street address.');
      return;
    }

    setSubmitting(true);
    setErrorMessage(null);

    const res = await GiftService.submitClaimAddress(token, {
      deliveryAddress: {
        street: street.trim(),
        city: city.trim(),
        state: state.trim(),
        coordinates,
      },
      notes: notes.trim() || undefined,
    });

    if (!res.success) {
      setErrorMessage(res.message);
      setSubmitting(false);
    } else {
      setIsClaimed(true);
      if (res.data) {
        setGiftDetails(res.data);
      }
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-orange-50 via-white to-green-50 py-16 px-4">
        <div className="text-center max-w-md w-full p-8 bg-white/80 backdrop-blur rounded-2xl shadow-xl border border-orange-100 animate-fadeIn">
          <div className="w-16 h-16 bg-orange-100 text-orange-600 rounded-full flex items-center justify-center mx-auto mb-4 animate-bounce">
            <Gift className="w-8 h-8" />
          </div>
          <h2 className="text-xl font-bold text-gray-900 mb-2">Unwrapping Gift Details...</h2>
          <p className="text-sm text-gray-500">Please wait while we verify your gift claim link.</p>
        </div>
      </div>
    );
  }

  if (isExpired) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 py-16 px-4">
        <div className="max-w-md w-full bg-white rounded-3xl p-8 shadow-xl text-center border border-gray-100">
          <div className="w-16 h-16 bg-amber-100 text-amber-600 rounded-full flex items-center justify-center mx-auto mb-4">
            <Clock className="w-8 h-8" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Gift Claim Link Expired</h2>
          <p className="text-sm text-gray-600 mb-6">
            This gift claim link was valid for 48 hours and has now expired. The sender has been automatically refunded.
          </p>
          <Link
            to="/"
            className="inline-flex items-center justify-center w-full px-6 py-3 bg-orange-600 hover:bg-orange-700 text-white font-medium rounded-xl transition"
          >
            Visit MarketMate Home
          </Link>
        </div>
      </div>
    );
  }

  if (errorMessage && !giftDetails) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 py-16 px-4">
        <div className="max-w-md w-full bg-white rounded-3xl p-8 shadow-xl text-center border border-gray-100">
          <div className="w-16 h-16 bg-red-100 text-red-600 rounded-full flex items-center justify-center mx-auto mb-4">
            <AlertCircle className="w-8 h-8" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Invalid Gift Link</h2>
          <p className="text-sm text-gray-600 mb-6">{errorMessage}</p>
          <Link
            to="/"
            className="inline-flex items-center justify-center w-full px-6 py-3 bg-gray-900 hover:bg-black text-white font-medium rounded-xl transition"
          >
            Return to Homepage
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 via-white to-green-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-3xl mx-auto">
        {/* Smart App Banner */}
        <div className="mb-6 bg-gradient-to-r from-orange-600 to-amber-600 text-white rounded-2xl p-4 shadow-lg flex flex-col sm:flex-row items-center justify-between gap-4 animate-fadeIn">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-white/20 rounded-xl">
              <Smartphone className="w-6 h-6 text-white" />
            </div>
            <div>
              <p className="text-xs font-medium uppercase tracking-wider text-orange-200">Mobile Experience</p>
              <h4 className="text-sm font-semibold">Have the MarketMate App on your phone?</h4>
            </div>
          </div>
          <button
            onClick={handleOpenInApp}
            className="w-full sm:w-auto px-4 py-2 bg-white text-orange-600 font-bold rounded-xl text-xs sm:text-sm hover:bg-orange-50 transition shadow-sm whitespace-nowrap"
          >
            Open in App
          </button>
        </div>

        {/* Main Gift Container */}
        <div className="bg-white rounded-3xl shadow-xl border border-orange-100 overflow-hidden">
          {/* Header Banner */}
          <div className="bg-gradient-to-r from-orange-500 via-amber-500 to-green-600 p-8 text-white relative overflow-hidden">
            <div className="absolute right-0 top-0 translate-x-8 -translate-y-8 opacity-10">
              <Gift className="w-64 h-64" />
            </div>
            <div className="relative z-10">
              <div className="inline-flex items-center gap-2 px-3 py-1 bg-white/20 backdrop-blur-md rounded-full text-xs font-semibold uppercase tracking-wider mb-4">
                <Sparkles className="w-4 h-4 text-yellow-300" />
                Special Gift Delivery
              </div>
              <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight mb-2">
                Hello, {giftDetails?.recipientName}! 🎁
              </h1>
              <p className="text-orange-100 text-sm sm:text-base max-w-xl">
                {giftDetails?.isAnonymous
                  ? 'A friend sent you a fresh farm produce gift package on MarketMate.'
                  : `${giftDetails?.senderDisplayName} sent you a fresh farm produce gift package on MarketMate.`}
              </p>
            </div>
          </div>

          <div className="p-6 sm:p-8 space-y-8">
            {/* Sender Note Callout */}
            {giftDetails?.giftMessage && (
              <div className="bg-orange-50/80 rounded-2xl p-5 border border-orange-200 relative">
                <div className="text-xs font-bold uppercase tracking-wider text-orange-800 mb-1 flex items-center gap-1.5">
                  <Gift className="w-4 h-4 text-orange-600" />
                  Personal Note from {giftDetails.senderDisplayName}
                </div>
                <p className="text-gray-800 text-sm sm:text-base italic leading-relaxed">
                  "{giftDetails.giftMessage}"
                </p>
              </div>
            )}

            {/* Package Contents */}
            <div>
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                  <Package className="w-5 h-5 text-green-600" />
                  Package Contents ({giftDetails?.items.length || 0} items)
                </h3>
                <span className="text-xs font-semibold text-green-700 bg-green-100 px-2.5 py-1 rounded-full flex items-center gap-1">
                  <ShieldCheck className="w-3.5 h-3.5" />
                  100% Free Gift
                </span>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {giftDetails?.items.map((item, idx) => (
                  <div
                    key={idx}
                    className="p-3.5 rounded-2xl bg-gray-50 border border-gray-200/80 flex items-center justify-between"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-white rounded-xl shadow-xs flex items-center justify-center text-orange-600 font-bold border border-gray-100">
                        {item.quantity}x
                      </div>
                      <div>
                        <h4 className="text-sm font-bold text-gray-900">{item.name}</h4>
                        <p className="text-xs text-gray-500 capitalize">{item.deliveryClass || 'Standard Delivery'}</p>
                      </div>
                    </div>
                    {item.isPerishable && (
                      <span className="text-[10px] font-bold text-red-600 bg-red-50 border border-red-200 px-2 py-0.5 rounded-md">
                        Fresh
                      </span>
                    )}
                  </div>
                ))}
              </div>

              {giftDetails?.sellerName && (
                <div className="mt-3 text-xs text-gray-500 flex items-center gap-1.5">
                  <Store className="w-3.5 h-3.5 text-gray-400" />
                  Sourced from: <span className="font-medium text-gray-700">{giftDetails.sellerName}</span> ({giftDetails.sellerCity})
                </div>
              )}
            </div>

            {/* Claim State: Form vs Already Claimed */}
            {isClaimed ? (
              <div className="bg-green-50 border border-green-200 rounded-2xl p-6 text-center animate-scaleIn">
                <div className="w-14 h-14 bg-green-100 text-green-600 rounded-full flex items-center justify-center mx-auto mb-3">
                  <CheckCircle2 className="w-8 h-8" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-1">Gift Package Claimed!</h3>
                <p className="text-sm text-gray-600 max-w-md mx-auto mb-6">
                  Your delivery address has been verified. The seller has been notified to pack your fresh items. You will receive updates via SMS.
                </p>
                <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                  <Link
                    to="/"
                    className="w-full sm:w-auto px-6 py-2.5 bg-green-700 hover:bg-green-800 text-white font-semibold rounded-xl text-sm transition"
                  >
                    Explore MarketMate
                  </Link>
                  <button
                    onClick={handleOpenInApp}
                    className="w-full sm:w-auto px-6 py-2.5 bg-white text-green-700 border border-green-300 font-semibold rounded-xl text-sm hover:bg-green-50 transition"
                  >
                    Track in Mobile App
                  </button>
                </div>
              </div>
            ) : (
              <div>
                <div className="border-t border-gray-100 pt-6">
                  <h3 className="text-lg font-bold text-gray-900 mb-2 flex items-center gap-2">
                    <MapPin className="w-5 h-5 text-orange-600" />
                    Where Should We Deliver Your Package?
                  </h3>
                  <p className="text-xs sm:text-sm text-gray-500 mb-6">
                    Enter your street address below so our dispatch rider can deliver your gift package directly to your doorstep.
                  </p>

                  {errorMessage && (
                    <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-xl text-red-700 text-xs sm:text-sm flex items-center gap-2">
                      <AlertCircle className="w-4 h-4 shrink-0" />
                      {errorMessage}
                    </div>
                  )}

                  <form onSubmit={handleSubmitClaim} className="space-y-4">
                    <div>
                      <div className="flex items-center justify-between mb-1">
                        <label className="block text-xs font-bold uppercase tracking-wider text-gray-700">
                          Street Address <span className="text-red-500">*</span>
                        </label>
                        <button
                          type="button"
                          onClick={handleAutoLocate}
                          disabled={locating}
                          className="text-xs text-orange-600 hover:text-orange-700 font-medium flex items-center gap-1"
                        >
                          <Navigation className="w-3 h-3" />
                          {locating ? 'Locating...' : 'Use My Current Location'}
                        </button>
                      </div>
                      <input
                        type="text"
                        required
                        value={street}
                        onChange={e => setStreet(e.target.value)}
                        placeholder="e.g. 15 Admiralty Way, Lekki Phase 1"
                        className="w-full px-4 py-3 bg-gray-50 border border-gray-300 rounded-xl text-sm focus:bg-white focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent transition"
                      />
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs font-bold uppercase tracking-wider text-gray-700 mb-1">
                          City <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          required
                          value={city}
                          onChange={e => setCity(e.target.value)}
                          placeholder="e.g. Lekki / Ikeja"
                          className="w-full px-4 py-3 bg-gray-50 border border-gray-300 rounded-xl text-sm focus:bg-white focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent transition"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-bold uppercase tracking-wider text-gray-700 mb-1">
                          State
                        </label>
                        <input
                          type="text"
                          value={state}
                          onChange={e => setState(e.target.value)}
                          placeholder="e.g. Lagos"
                          className="w-full px-4 py-3 bg-gray-50 border border-gray-300 rounded-xl text-sm focus:bg-white focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent transition"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-xs font-bold uppercase tracking-wider text-gray-700 mb-1">
                        Delivery Instructions / Landmark (Optional)
                      </label>
                      <input
                        type="text"
                        value={notes}
                        onChange={e => setNotes(e.target.value)}
                        placeholder="e.g. Beside Zenith Bank, call when outside gate"
                        className="w-full px-4 py-3 bg-gray-50 border border-gray-300 rounded-xl text-sm focus:bg-white focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent transition"
                      />
                    </div>

                    <button
                      type="submit"
                      disabled={submitting}
                      className="w-full py-4 bg-gradient-to-r from-orange-600 to-amber-600 hover:from-orange-700 hover:to-amber-700 text-white font-bold rounded-2xl shadow-lg hover:shadow-xl transition transform active:scale-99 flex items-center justify-center gap-2 text-base"
                    >
                      {submitting ? (
                        <>Claiming Gift Package...</>
                      ) : (
                        <>
                          Claim My Gift Package <ArrowRight className="w-5 h-5" />
                        </>
                      )}
                    </button>
                  </form>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* App Coming Soon / Deep Link Helper Modal */}
      {showAppModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-xs z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl max-w-sm w-full p-6 text-center shadow-2xl animate-scaleIn">
            <div className="w-16 h-16 bg-orange-100 text-orange-600 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <Smartphone className="w-8 h-8" />
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-2">MarketMate Mobile App</h3>
            <p className="text-xs sm:text-sm text-gray-600 mb-6 leading-relaxed">
              Our native mobile app for iOS & Android is launching soon on the App Store & Google Play. You can complete your gift claim right here on this web page!
            </p>
            <button
              onClick={() => setShowAppModal(false)}
              className="w-full py-3 bg-orange-600 hover:bg-orange-700 text-white font-bold rounded-xl text-sm transition"
            >
              Continue on Web
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default GiftClaim;
